import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Input,
  SpinnerLoader,
} from 'lift-ui';
import { Search, RefreshCw, Plus } from 'lucide-react';

import { Toastify } from 'lift-ui';
import apiClient from '@lift-admin/utils/apiClient';
import apiClientDrawerV2 from '@lift-admin/utils/apiClientDrawerV2';
import { useAuthContext } from '@lift-admin/utils/provider';
import Items from './drawer-parts/items';
import Groupings from './drawer-parts/groupings';
import Applications from './drawer-parts/applications';
import Reports from './drawer-parts/report';
import UIComponents from './drawer-parts/ui-components';
import Channels from './drawer-parts/channels';
import CustomComponents from './drawer-parts/custom-components';
import ReportsSandbox from './drawer-parts/report-sandbox';
import DefaultSidebarItems from './drawer-parts/default-sidebar-items';

const DRAWER_TAB_VALUES = [
  'items',
  'reports',
  'reports-sandbox',
  'groupings',
  'channels',
  'applications',
  'ui-components',
  'custom-components',
  'default-sidebar-items',
] as const;

const DrawerStructure = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialTab =
    tabFromUrl && DRAWER_TAB_VALUES.includes(tabFromUrl as (typeof DRAWER_TAB_VALUES)[number])
      ? tabFromUrl
      : 'items';

  const { canEdit, canManage, loading: authLoading } = useAuthContext();
  const hasEditAccess = !authLoading && canEdit('drawer_mgmt');
  const hasManageAccess = !authLoading && canManage('drawer_mgmt');

  const [toggle, setToggle] = useState<any>(initialTab);
  const [modules, setModules] = useState<any>([]);
  const [channels, setChannels] = useState<any>([]);
  const [groupings, setGroupings] = useState<any>([]);
  const [component, setComponent] = useState<any>([]);
  const [customComponents, setCustomComponents] = useState<any>([]);
  const [drawerItemsDynamic, setDrawerItemsDynamic] = useState<any>([]);
  const [reports, setReports] = useState<any>([]);
  const [reportsSandbox, setReportsSandbox] = useState<any>([]);
  const [organizations, setOrganization] = useState([]);
  const [brands, setBrands] = useState([]);
  // for post request

  // for dialog
  const [openDialog, setOpenDialog] = useState<any>({
    item: false,
    grouping: false,
    channel: false,
    application: false,
    uiComponent: false,
    customComponent: false,
    reportSandbox: false,
    report: false,
    defaultSidebarItem: false,
  });

  const [loadData, setLoadData] = useState<boolean>(false);

  const updateDataOnToggle = (toggleActionName: any) => {
    setSearchText('');
    setToggle(toggleActionName);
    setSearchParams(
      toggleActionName === 'items' ? {} : { tab: toggleActionName },
      { replace: true }
    );
  };
  const getModules = async () => {
    setLoadData(true);
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClientDrawerV2.get('/admin/applications', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      // New API returns array directly, no nested response
      setModules(res?.data);
    } catch {
      Toastify('error', 'Applications not fetched');
    } finally {
      setLoadData(false);
    }
  };
  const getChannels = async () => {
    setLoadData(true);
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClientDrawerV2.get('/admin/channels', {
        params: {
          include_inactive: true,
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      // New API returns array directly, no nested response
      setChannels(res?.data);
    } catch {
      Toastify('error', 'Channels not fetched');
    } finally {
      setLoadData(false);
    }
  };
  const getDrawrGroupings = async () => {
    setLoadData(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') as any);
      const res = await apiClientDrawerV2.get('/admin/groupings', {
        params: {
          include_inactive: true,
          user_id: user?.id,
          is_admin: true,
          is_team: false
        }
      });

      setGroupings(res?.data || []);
    } catch {
      Toastify('error', 'Groupings not fetched');
    } finally {
      setLoadData(false);
    }
  };
  const getComponents = async () => {
    setLoadData(true);
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClientDrawerV2.get('/admin/ui-components', {
        params: {
          user_id: user.id,
          is_admin: true,
          is_team: false,
        }
      });

      setComponent(res?.data);
    } catch {
      Toastify('error', 'Components not fetched');
    } finally {
      setLoadData(false);
    }
  };
  const getCustomComponents = async () => {
    setLoadData(true);
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClientDrawerV2.get('/admin/custom-components', {
        params: {
          user_id: user.id,
          is_admin: true,
          is_team: true,
        }
      });

      // New API returns array directly, no nested response
      setCustomComponents(res?.data);
    } catch {
      Toastify('error', 'Components not fetched');
    } finally {
      setLoadData(false);
    }
  };
  const getDrawerItemsDynamic = async () => {
    try {
      const res = await apiClientDrawerV2.get(
        '/drawer/v1/admin/drawer/structure/drawer-items-dynamic'
      );

      setDrawerItemsDynamic(res?.data?.response?.drawerItems);
    } catch {
      // Silently fail - this endpoint may not be available and the data is not actively used
      console.warn('Drawer items dynamic not fetched');
    }
  };
  const getReports = async () => {
    setLoadData(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') as any);
      const res = await apiClientDrawerV2.get('/admin/reports', {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
          is_sandbox: false,
        }
      });

      const data = res?.data || [];
      setReports(data);
      console.log("[DrawerStructure] getReports (parent reports for dropdown): count =", data.length, "sample:", data[0] ? { id: data[0].id, display_name: data[0].display_name, is_sandbox: data[0].is_sandbox, used_in: data[0].used_in } : null);
    } catch {
      Toastify('error', 'Reports not fetched');
    } finally {
      setLoadData(false);
    }
  };

  const getReportsSandbox = async () => {
    setLoadData(true);
    try {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      const res = await apiClientDrawerV2.get("/admin/reports", {
        params: {
          user_id: user?.id,
          brand_id: user?.brand_id,
          is_admin: true,
          is_team: false,
          is_sandbox: true,
        }
      });

      // Sort by created_at in descending order (newest first)
      const sortedReports = res?.data?.sort(
        (a: { created_at: string | number | Date }, b: { created_at: string | number | Date }) => {
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        }
      );

      setReportsSandbox(sortedReports || []);
    } catch {
      Toastify("error", "Reports Sandbox not fetched");
    } finally {
      setLoadData(false);
    }
  };

  const user = JSON.parse(localStorage.getItem('user') as any);
  const getAllbrands = async () => {
    const res = await apiClient.get(
      `/brand/v1/admin/brands/allFields/${user.id}`
    );
    ;

    setBrands(res.data.response);
  };
  const customSortBasedOnDate = (a: any, b: any) => {
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  };

  const getAllOrgnaizations = async () => {
    const res = await apiClient.get(`/brand/v1/organizations/all`);
    setOrganization(res.data.response.sort(customSortBasedOnDate).reverse());
  };

  const callAllApi = async () => {
    getAllbrands();
    getAllOrgnaizations();
    getChannels();
    getModules();
    getDrawrGroupings();
    getComponents();
    getCustomComponents();
    getReports();
    getReportsSandbox();
  };

  useEffect(() => {
    callAllApi();
  }, []);

  const handleAddClick = (toggle: string) => {
    if (toggle === 'items') {
      setOpenDialog((prev: any) => ({ ...prev, item: true }));
    }
    if (toggle === 'groupings') {
      setOpenDialog((prev: any) => ({ ...prev, grouping: true }));

    }
    if (toggle === 'channels') {
      setOpenDialog((prev: any) => ({ ...prev, channel: true }));
    }
    if (toggle === 'applications') {
      setOpenDialog((prev: any) => ({ ...prev, application: true }));
    }
    if (toggle === 'ui-components') {
      setOpenDialog((prev: any) => ({ ...prev, uiComponent: true }));
    }
    if (toggle === 'custom-components') {
      setOpenDialog((prev: any) => ({ ...prev, customComponent: true }));
    }

    if (toggle === 'reports') {
      setOpenDialog((prev: any) => ({ ...prev, report: true }));
    }
    if (toggle === 'reports-sandbox') {
      setOpenDialog((prev: any) => ({ ...prev, reportSandbox: true }));
    }
    if (toggle === 'default-sidebar-items') {
      setOpenDialog((prev: any) => ({ ...prev, defaultSidebarItem: true }));
    }
  };

  const [searchText, setSearchText] = useState<any>('');

  useEffect(() => {
    setSearchText('');
  }, [toggle]);

  // Sync tab from URL when user uses browser back/forward
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const tab =
      urlTab && DRAWER_TAB_VALUES.includes(urlTab as (typeof DRAWER_TAB_VALUES)[number])
        ? urlTab
        : 'items';
    if (tab !== toggle) {
      updateDataOnToggle(tab);
    }
  }, [searchParams]);

  const getAddButtonLabel = () => {
    switch (toggle) {
      case 'items':
        return 'Add Drawer Item';
      case 'groupings':
        return 'Add Grouping';
      case 'reports':
        return 'Add Report';
      case 'reports-sandbox':
        return 'Add Report Sandbox';
      default:
        return 'Add Item';
    }
  };

  const showAddButton = toggle !== 'channels' &&
    toggle !== 'applications' &&
    toggle !== 'ui-components' &&
    toggle !== 'custom-components' &&
    toggle !== 'default-sidebar-items';
  
  const showDefaultSidebarAddButton = toggle === 'default-sidebar-items';

  const handleRefresh = () => {
    getModules();
    getChannels();
    getDrawrGroupings();
    getComponents();
    getCustomComponents();
    getDrawerItemsDynamic();
    getReports();
    getReportsSandbox();
  };

  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader
        title="Drawer Structure"
        renderCustomTop={
          <div className="flex items-center gap-4 w-full">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loadData}
              className="flex items-center gap-2 h-8 px-3"
            >
              {loadData ? (
                <SpinnerLoader className="h-4 w-4" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
            {hasEditAccess && showAddButton && (
              <Button onClick={() => handleAddClick(toggle)}>
                <Plus className="h-4 w-4 mr-2" />
                {getAddButtonLabel()}
              </Button>
            )}
            {hasEditAccess && showDefaultSidebarAddButton && (
              <Button onClick={() => setOpenDialog((prev: any) => ({ ...prev, defaultSidebarItem: true }))}>
                <Plus className="h-4 w-4 mr-2" />
                Add Default Item
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        }
      />

      <div className="px-8 pt-4 pb-4">
        <Tabs
          value={
            searchParams.get('tab') &&
            DRAWER_TAB_VALUES.includes(searchParams.get('tab') as (typeof DRAWER_TAB_VALUES)[number])
              ? searchParams.get('tab')!
              : 'items'
          }
          onValueChange={(value) => {
            updateDataOnToggle(value);
            if (value === 'items') {
              callAllApi();
            }
          }}
        >
          <TabsList className="w-fit">
            <TabsTrigger value="items">Drawer Items</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            <TabsTrigger value="reports-sandbox">Looker Studio Reports Sandbox</TabsTrigger>
            <TabsTrigger value="groupings">Groupings</TabsTrigger>
            <TabsTrigger value="channels">Channels</TabsTrigger>
            <TabsTrigger value="applications">Applications</TabsTrigger>
            <TabsTrigger value="ui-components">UI Components</TabsTrigger>
            <TabsTrigger value="custom-components">Custom Components</TabsTrigger>
            <TabsTrigger value="default-sidebar-items">Default Sidebar Items</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden px-8 pb-6 flex flex-col">
        {/* Content */}
        {toggle === 'items' && (
          <Items
            searchText={searchText}
            toggle={toggle}
            channels={channels}
            groupings={groupings}
            component={component}
            drawerItemsDynamic={drawerItemsDynamic}
            customComponents={customComponents}
            reports={reports}
            reportsSandbox={reportsSandbox}
            openDialog={openDialog?.item}
            setOpenDialog={(value: boolean) =>
              setOpenDialog((prev: any) => ({ ...prev, item: value }))
            }
            brands={brands}
            organizations={organizations}
            applications={modules}
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}
        {toggle === 'groupings' && (
          <Groupings
            searchText={searchText}
            toggle={toggle}
            channels={channels}
            applications={modules}
            openDialog={openDialog?.grouping}
            brands={brands}
            setOpenDialog={(value: boolean) =>
              setOpenDialog((prev: any) => ({ ...prev, grouping: value }))
            }
            organizations={organizations}
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}
        {toggle === 'applications' && (
          <Applications
            searchText={searchText}
            toggle={toggle}
            brands={brands}
            openDialog={openDialog?.application}
            setOpenDialog={(value: boolean) =>
              setOpenDialog((prev: any) => ({ ...prev, application: value }))
            }
            organizations={organizations}
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}
        {toggle === 'channels' && (
          <Channels
            brands={brands}
            searchText={searchText}
            toggle={toggle}
            openDialog={openDialog?.channel}
            setOpenDialog={(value: boolean) =>
              setOpenDialog((prev: any) => ({ ...prev, channel: value }))
            }
            organizations={organizations}
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}
        {/* {toggle === "report" && (

          <Reports toggle={toggle} drawerStructure={drawerStructure} />
        )} */}
        {toggle === 'ui-components' && (
          <UIComponents
            searchText={searchText}
            toggle={toggle}
            openDialog={openDialog?.uiComponent}
            setOpenDialog={(value: boolean) =>
              setOpenDialog((prev: any) => ({ ...prev, uiComponent: value }))
            }
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}
        {toggle === 'custom-components' && (
          <CustomComponents
            searchText={searchText}
            toggle={toggle}
            openDialog={openDialog?.customComponent}
            setOpenDialog={(value: boolean) =>
              setOpenDialog((prev: any) => ({
                ...prev,
                customComponent: value,
              }))
            }
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}
        {toggle === 'reports' && (
          <Reports
            searchText={searchText}
            setOpenDialog={setOpenDialog}
            toggle={toggle}
            openDialog={openDialog?.report}
            channels={channels}
            modules={[]}
            applications={modules}
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}

        {toggle === 'reports-sandbox' && (
          <ReportsSandbox
            searchText={searchText}
            setOpenDialog={setOpenDialog}
            toggle={toggle}
            openDialog={openDialog?.reportSandbox}
            applications={modules}
            channels={channels}
            reports={reports}
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}

        {toggle === 'default-sidebar-items' && (
          <DefaultSidebarItems
            searchText={searchText}
            toggle={toggle}
            openDialog={openDialog?.defaultSidebarItem}
            setOpenDialog={(value: boolean) =>
              setOpenDialog((prev: any) => ({ ...prev, defaultSidebarItem: value }))
            }
            hasEditAccess={hasEditAccess}
            hasManageAccess={hasManageAccess}
          />
        )}
      </div>
    </div>
  );
};

export default DrawerStructure;
